import React from "react";
import Header from "parts/Header";
import Footer from "parts/Footer";
import {
  Fade,
  Slide,
  Zoom,
  Roll,
  Bounce,
  AttentionSeeker,
} from "react-awesome-reveal";
import Discuss from "parts/Discuss";

const BusinessStructurePage = () => {
  const divisions = [
    {
      title: "Managing Partners",
      icon: "👔",
      description:
        "Executive leadership providing strategic direction and client relationship management",
      responsibilities: [
        "Strategic Vision",
        "Client Relations",
        "Partnership Development",
        "Corporate Governance",
      ],
      teamSize: "3 Partners",
      gradient: "from-purple-500 to-indigo-600",
      level: "executive",
    },
    {
      title: "Venture Analysts",
      icon: "📈",
      description:
        "Investment professionals specializing in market analysis and funding opportunities",
      responsibilities: [
        "Market Research",
        "Due Diligence",
        "Investment Analysis",
        "Portfolio Management",
      ],
      teamSize: "8 Analysts",
      gradient: "from-blue-500 to-cyan-600",
      level: "senior",
    },
    {
      title: "Tax Professionals",
      icon: "🧮",
      description:
        "Certified experts in tax planning, compliance, and financial advisory services",
      responsibilities: [
        "Tax Planning",
        "Compliance Management",
        "Financial Advisory",
        "Audit Support",
      ],
      teamSize: "12 CPAs",
      gradient: "from-emerald-500 to-teal-600",
      level: "specialist",
    },
    {
      title: "Real Estate Teams",
      icon: "🏢",
      description:
        "Property development specialists and real estate investment advisors",
      responsibilities: [
        "Property Analysis",
        "Development Planning",
        "Investment Strategy",
        "Market Evaluation",
      ],
      teamSize: "6 Specialists",
      gradient: "from-orange-500 to-red-600",
      level: "specialist",
    },
    {
      title: "Technology Liaisons",
      icon: "💻",
      description:
        "Digital transformation experts ensuring seamless technology integration",
      responsibilities: [
        "Tech Integration",
        "Digital Strategy",
        "System Optimization",
        "Innovation Support",
      ],
      teamSize: "5 Engineers",
      gradient: "from-pink-500 to-purple-600",
      level: "support",
    },
    {
      title: "Compliance Officers",
      icon: "⚖️",
      description:
        "Regulatory experts ensuring adherence to all legal and compliance requirements",
      responsibilities: [
        "Regulatory Oversight",
        "Risk Assessment",
        "Policy Development",
        "Legal Coordination",
      ],
      teamSize: "4 Officers",
      gradient: "from-cyan-500 to-blue-600",
      level: "specialist",
    },
  ];

  const getWidthClass = (level) => {
    switch (level) {
      case "executive":
        return "w-full";
      case "senior":
        return "w-4/5";
      case "specialist":
        return "w-3/5";
      default:
        return "w-2/5";
    }
  };

  const organizationStats = [
    { label: "Total Team Members", value: "38+", icon: "👥" },
    { label: "Years Combined Experience", value: "200+", icon: "🎯" },
    { label: "Service Divisions", value: "6", icon: "🏗️" },
    { label: "Client Satisfaction", value: "98%", icon: "⭐" },
  ];

  return (
    <>
      <Header />

      {/* Hero Section */}
      <div className="relative flex items-center min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        {/* Animated Background Network */}
        <div className="absolute inset-0 overflow-hidden opacity-20">
          {/* Network Nodes */}
          <div className="absolute w-4 h-4 rounded-full top-1/4 left-1/4 bg-cyan-400 animate-pulse" />
          <div className="absolute w-6 h-6 delay-300 bg-purple-400 rounded-full top-1/3 right-1/4 animate-pulse" />
          <div className="absolute w-5 h-5 rounded-full bottom-1/3 left-1/3 bg-emerald-400 animate-pulse delay-600" />
          <div className="absolute w-4 h-4 bg-pink-400 rounded-full bottom-1/4 right-1/3 animate-pulse delay-900" />
          <div className="absolute w-8 h-8 delay-150 bg-yellow-400 rounded-full top-1/2 left-1/2 animate-pulse" />

          {/* Connecting Lines */}
          <svg className="absolute inset-0 w-full h-full">
            <line
              x1="25%"
              y1="25%"
              x2="75%"
              y2="33%"
              stroke="rgba(147, 197, 253, 0.3)"
              strokeWidth="2"
              strokeDasharray="5,5"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="0;10"
                dur="2s"
                repeatCount="indefinite"
              />
            </line>
            <line
              x1="33%"
              y1="67%"
              x2="67%"
              y2="75%"
              stroke="rgba(196, 181, 253, 0.3)"
              strokeWidth="2"
              strokeDasharray="5,5"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="0;10"
                dur="3s"
                repeatCount="indefinite"
              />
            </line>
            <line
              x1="50%"
              y1="50%"
              x2="75%"
              y2="33%"
              stroke="rgba(167, 243, 208, 0.3)"
              strokeWidth="2"
              strokeDasharray="5,5"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="0;10"
                dur="2.5s"
                repeatCount="indefinite"
              />
            </line>
          </svg>
        </div>

        {/* Gradient Orbs */}
        <div className="absolute rounded-full top-20 left-20 w-72 h-72 bg-gradient-to-r from-cyan-400 to-blue-600 mix-blend-multiply filter blur-xl opacity-70 animate-pulse" />
        <div className="absolute delay-1000 rounded-full bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-600 mix-blend-multiply filter blur-xl opacity-70 animate-pulse" />

        <div className="container relative z-10 px-6 mx-auto">
          <div className="text-center text-white">
            <Roll triggerOnce>
              <div className="inline-block mb-8">
                <div className="flex items-center justify-center w-32 h-32 mx-auto text-6xl transition-transform duration-700 transform shadow-2xl bg-gradient-to-r from-emerald-400 to-cyan-500 rounded-3xl rotate-12 hover:rotate-0">
                  🏗️
                </div>
              </div>
            </Roll>

            <Fade direction="up" triggerOnce duration={1000}>
              <h1 className="mb-6 text-6xl font-black leading-tight text-transparent md:text-8xl bg-gradient-to-r from-white via-cyan-200 to-emerald-200 bg-clip-text">
                BUSINESS
              </h1>
            </Fade>

            <Fade direction="up" delay={300} triggerOnce>
              <h2 className="mb-8 text-4xl font-light md:text-5xl text-cyan-200">
                Structure & Organization
              </h2>
            </Fade>

            <Fade direction="up" delay={600} triggerOnce>
              <p className="max-w-4xl mx-auto mb-12 text-xl leading-relaxed text-gray-300 md:text-2xl">
                Katella Venture Capital LLC operates through specialized
                divisions, each led by experienced professionals delivering
                excellence in their respective fields
              </p>
            </Fade>

            <Bounce delay={1000} triggerOnce>
              <div className="inline-flex items-center px-8 py-4 space-x-3 border rounded-full bg-white/10 backdrop-blur-md border-white/20">
                <span className="text-lg font-medium">
                  38+ Expert Professionals
                </span>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-500">
                  <span className="text-xs text-white">✓</span>
                </div>
              </div>
            </Bounce>
          </div>
        </div>
      </div>

      {/* Organization Stats */}
      <div className="py-16 bg-gradient-to-r from-gray-900 to-slate-900">
        <div className="container px-6 mx-auto">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {organizationStats.map((stat, index) => (
              <Zoom delay={index * 150} triggerOnce key={stat.label}>
                <div className="text-center group">
                  <div className="mb-4 text-5xl transition-transform duration-300 transform group-hover:scale-110">
                    {stat.icon}
                  </div>
                  <div className="mb-2 text-4xl font-black text-transparent text-white bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text">
                    {stat.value}
                  </div>
                  <div className="font-medium text-gray-300">{stat.label}</div>
                </div>
              </Zoom>
            ))}
          </div>
        </div>
      </div>

      {/* Division Structure */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container px-6 mx-auto">
          {/* Section Header */}
          <Fade direction="up" triggerOnce>
            <div className="mb-20 text-center">
              <h3 className="mb-6 text-5xl font-bold text-gray-800">
                Organizational Divisions
              </h3>
              <div className="w-32 h-1 mx-auto mb-8 bg-gradient-to-r from-emerald-500 to-cyan-500" />
              <p className="max-w-3xl mx-auto text-xl text-gray-600">
                Six specialized divisions working in harmony to deliver
                comprehensive business solutions
              </p>
            </div>
          </Fade>

          {/* Divisions Grid */}
          <div className="grid gap-8 mb-20 lg:grid-cols-2 xl:grid-cols-3">
            {divisions.map((division, index) => (
              <Slide
                key={division.title}
                direction={
                  // eslint-disable-next-line no-nested-ternary
                  index % 3 === 0 ? "left" : index % 3 === 1 ? "up" : "right"
                }
                delay={index * 100}
                triggerOnce
              >
                <div className="relative h-full overflow-hidden transition-all duration-500 transform bg-white border border-gray-100 shadow-xl group rounded-3xl hover:shadow-2xl hover:-translate-y-6">
                  {/* Level Indicator */}
                  <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-gray-300 to-gray-400">
                    <div
                      className={`h-full bg-gradient-to-r ${
                        division.gradient
                      } transition-all duration-500 ${getWidthClass(
                        // eslint-disable-next-line comma-dangle
                        division.level
                      )}`}
                    />
                  </div>

                  {/* Team Size Badge */}
                  <div className="absolute z-20 top-4 right-4">
                    <div
                      className={`bg-gradient-to-r ${division.gradient} text-white px-3 py-1 rounded-full text-xs font-bold`}
                    >
                      {division.teamSize}
                    </div>
                  </div>

                  <div className="relative z-10 p-8">
                    {/* Icon */}
                    <div className="mb-6 text-6xl transition-all duration-500 transform group-hover:scale-110 group-hover:rotate-12">
                      {division.icon}
                    </div>

                    {/* Title */}
                    <h4 className="mb-4 text-2xl font-bold text-gray-800 group-hover:text-gray-900">
                      {division.title}
                    </h4>

                    {/* Description */}
                    <p className="mb-6 leading-relaxed text-gray-600 group-hover:text-gray-700">
                      {division.description}
                    </p>

                    {/* Responsibilities */}
                    <div className="mb-6 space-y-3">
                      <h5 className="text-sm font-semibold tracking-wide text-gray-800 uppercase">
                        Key Responsibilities:
                      </h5>
                      <div className="grid grid-cols-2 gap-2">
                        {division.responsibilities.map((responsibility) => (
                          <div
                            key={responsibility}
                            className="flex items-center space-x-2"
                          >
                            <div
                              className={`w-2 h-2 rounded-full bg-gradient-to-r ${division.gradient} opacity-80`}
                            />
                            <span className="text-xs text-gray-600">
                              {responsibility}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Bottom Accent */}
                    <div
                      className={`absolute bottom-0 left-0 w-full h-2 bg-gradient-to-r ${division.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}
                    />
                  </div>
                </div>
              </Slide>
            ))}
          </div>

          {/* Featured Image & Organizational Philosophy */}
          <Zoom triggerOnce>
            <div className="max-w-6xl mx-auto mb-20">
              <div className="relative overflow-hidden shadow-2xl rounded-3xl group">
                <img
                  src="https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=1200&q=80"
                  alt="Business Structure and Team Collaboration"
                  className="object-cover w-full transition-transform duration-700 transform h-96 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-r from-emerald-600/30 to-cyan-600/30 group-hover:opacity-100" />

                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h4 className="mb-4 text-3xl font-bold">
                    Collaborative Excellence
                  </h4>
                  <p className="max-w-3xl text-lg text-gray-200">
                    Our integrated structure ensures seamless collaboration
                    across all divisions, providing clients with comprehensive
                    support from initial consultation through successful project
                    completion.
                  </p>
                </div>
              </div>
            </div>
          </Zoom>

          {/* Organizational Philosophy */}
          <Fade direction="up" triggerOnce>
            <div className="max-w-4xl mx-auto text-center">
              <div className="relative p-12 overflow-hidden border bg-gradient-to-r from-emerald-50 to-cyan-50 rounded-3xl border-emerald-100">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500" />

                <h3 className="mb-8 text-3xl font-bold text-gray-800">
                  Our Organizational Philosophy
                </h3>

                <div className="grid gap-8 md:grid-cols-3">
                  <Fade direction="up" delay={200} triggerOnce>
                    <div className="text-center">
                      <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 text-2xl font-bold text-white rounded-full bg-gradient-to-br from-emerald-400 to-cyan-600">
                        🎯
                      </div>
                      <h4 className="mb-2 font-semibold text-gray-800">
                        Specialized Expertise
                      </h4>
                      <p className="text-sm text-gray-600">
                        Each division maintains deep expertise in their field
                        while contributing to our unified vision
                      </p>
                    </div>
                  </Fade>

                  <Fade direction="up" delay={400} triggerOnce>
                    <div className="text-center">
                      <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 text-2xl font-bold text-white rounded-full bg-gradient-to-br from-cyan-400 to-blue-600">
                        🤝
                      </div>
                      <h4 className="mb-2 font-semibold text-gray-800">
                        Collaborative Integration
                      </h4>
                      <p className="text-sm text-gray-600">
                        Seamless coordination ensures comprehensive solutions
                        for every client engagement
                      </p>
                    </div>
                  </Fade>

                  <Fade direction="up" delay={600} triggerOnce>
                    <div className="text-center">
                      <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 text-2xl font-bold text-white rounded-full bg-gradient-to-br from-blue-400 to-purple-600">
                        📈
                      </div>
                      <h4 className="mb-2 font-semibold text-gray-800">
                        Continuous Growth
                      </h4>
                      <p className="text-sm text-gray-600">
                        Our structure evolves to meet changing market demands
                        and client expectations
                      </p>
                    </div>
                  </Fade>
                </div>
              </div>
            </div>
          </Fade>

          {/* Call to Action */}
          <Fade direction="up" delay={500} triggerOnce>
            <div className="mt-20 text-center">
              <AttentionSeeker effect="heartBeat" delay={2000} triggerOnce>
                <div className="relative inline-block">
                  <div className="absolute inset-0 rounded-full opacity-75 bg-gradient-to-r from-emerald-500 to-cyan-600 blur-lg animate-pulse" />
                  <button
                    type="button"
                    className="relative px-12 py-6 text-lg font-bold text-white transition-all duration-300 transform rounded-full shadow-2xl bg-gradient-to-r from-emerald-600 to-cyan-600 hover:scale-105"
                  >
                    Meet Our Expert Teams
                  </button>
                </div>
              </AttentionSeeker>

              <p className="max-w-2xl mx-auto mt-6 text-gray-600">
                Experience the power of our integrated organizational structure.
                Connect with our specialized divisions to accelerate your
                business success.
              </p>
            </div>
          </Fade>
        </div>
      </div>

      <Discuss />
      <Footer />
    </>
  );
};

export default BusinessStructurePage;
